import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { LogIn, FileText, Clock, CheckCircle2, LogOut, MessageSquare, DollarSign, Gift, Copy } from "lucide-react";
import PoweredBy from "@/components/PoweredBy";

const STATUS_MAP = {
  new: { label: "Received", tone: "bg-secondary text-foreground" },
  reviewed: { label: "In progress", tone: "bg-accent/15 text-accent" },
  completed: { label: "Filed", tone: "bg-primary/15 text-primary" },
};

const UPLOAD_LABELS = {
  drivers_license: "Driver's license / ID",
  ssn_proof: "SSN cards",
  proof_of_residency: "Residency",
  form_1098t: "1098-T",
  form_1095: "1095",
  form_w2: "W-2",
  form_1099: "1099",
  child_care: "Child care",
  self_employment_receipts: "Self-employment receipts",
  charitable_donations: "Charitable donations",
  mortgage_forms: "Mortgage",
  retirement_forms: "Retirement",
};

export default function ClientPortal() {
  const { branding } = useBranding();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [intake, setIntake] = useState(null);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/client/intake/lookup", { email, access_code: code });
      setIntake({ ...r.data, _access_code: code.toUpperCase() });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not find your intake");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => { setIntake(null); setEmail(""); setCode(""); };

  if (intake) return <ClientView intake={intake} onLogout={logout} branding={branding} />;

  return (
    <div className="min-h-screen bg-background paper-texture">
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" /> :
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">{branding.brand_name?.[0]}</div>}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <div className="label-caps hidden sm:block">Client portal</div>
        </div>
      </header>

      <section className="max-w-md mx-auto p-6 py-16">
        <div className="label-caps mb-3">Check your status</div>
        <h1 className="font-serif text-4xl mb-3">Where's my return?</h1>
        <p className="text-muted-foreground text-sm mb-8">
          Enter the email you used on your intake or amendment form and the 6-character access code from your submission receipt.
        </p>

        <form onSubmit={login} className="space-y-4" data-testid="client-portal-form">
          <div className="space-y-1">
            <Label className="label-caps text-[10px]">Email on the intake</Label>
            <Input required type="email" data-testid="portal-email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="label-caps text-[10px]">Access code</Label>
            <Input
              required
              data-testid="portal-code"
              placeholder="ABC123"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="font-mono tracking-widest text-center text-lg"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full" size="lg" data-testid="portal-submit-btn">
            <LogIn size={14} className="mr-2" /> {loading ? "Looking up…" : "Look up my return"}
          </Button>
        </form>

        <div className="mt-8 border-t border-border pt-6 text-xs text-muted-foreground space-y-2">
          <p>Haven't submitted yet? <Link to="/intake" className="underline text-foreground">Start your intake →</Link></p>
          <p>Need to fix a prior return? <Link to="/amend" className="underline text-foreground">Request an amendment →</Link></p>
          <p>Lost your code? <a href={`mailto:${branding.email || "support@dynamicfinancialsws.online"}`} className="underline text-foreground">Email us</a> — we'll send it to the address on file.</p>
        </div>

        <PoweredBy className="text-center mt-10" />
      </section>
    </div>
  );
}

function ClientView({ intake, onLogout, branding }) {
  const isAmendment = intake.kind === "amendment";
  const st = STATUS_MAP[intake.status] || STATUS_MAP.new;
  const updates = (intake.status_updates || []).slice().reverse();
  const totalDocs = Object.values(intake.uploads || {}).reduce((n, arr) => n + (arr?.length || 0), 0);
  const uploadLabels = isAmendment ? AMEND_UPLOAD_LABELS : UPLOAD_LABELS;

  return (
    <div className="min-h-screen bg-background paper-texture">
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" /> :
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">{branding.brand_name?.[0]}</div>}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <Button size="sm" variant="ghost" onClick={onLogout} data-testid="portal-logout">
            <LogOut size={12} className="mr-1" /> Sign out
          </Button>
        </div>
      </header>

      <section className="max-w-3xl mx-auto p-6 lg:p-10 space-y-8" data-testid="portal-view">
        <div>
          <div className="label-caps mb-2">{isAmendment ? `Your amendment · TY${intake.tax_year}` : "Your return"}</div>
          <h1 className="font-serif text-4xl mb-2">Hi, {intake.first_name}.</h1>
          <div className="flex items-center gap-3 mb-1">
            <span className={`label-caps text-[10px] px-3 py-1 rounded ${st.tone}`} data-testid="portal-status">{st.label}</span>
            <span className="text-xs text-muted-foreground">Submitted {new Date(intake.submitted_at).toLocaleDateString()}</span>
          </div>
          {isAmendment && (
            <div className="text-xs text-muted-foreground mt-2">
              Reason: <strong>{REASON_LABELS[intake.reason] || intake.reason}</strong> · Fee: ${intake.fee || 75}
            </div>
          )}
        </div>

        {/* Timeline of updates */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4 text-primary">
            <MessageSquare size={14} />
            <span className="label-caps text-[10px]">Updates from your preparer</span>
          </div>
          {updates.length === 0 ? (
            <div className="text-sm text-muted-foreground flex items-start gap-3">
              <Clock size={14} className="mt-0.5" />
              <div>
                No updates yet — we'll post here as your {isAmendment ? "amendment" : "return"} moves through review, filing, and refund. Check back in a day or two.
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="portal-updates">
              {updates.map((u) => (
                <div key={u.id} className="border-l-2 border-primary pl-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="label-caps text-[9px]">{STAGE_LABELS[u.stage] || u.stage}</span>
                    <span className="text-[10px] text-muted-foreground">{new Date(u.posted_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{u.message}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {!isAmendment && intake.disclaimer_cash_advance && (
          <CashAdvanceCard intake={intake} />
        )}

        {!isAmendment && (
          <ReferralCard intake={intake} />
        )}

        {/* Summary */}
        <Card className="p-6">
          <div className="label-caps text-[10px] mb-3">On file</div>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <KV k="Name" v={`${intake.first_name} ${intake.last_name}`} />
            {isAmendment ? (
              <>
                <KV k="Tax year" v={`TY${intake.tax_year}`} />
                <KV k="Reason" v={REASON_LABELS[intake.reason] || intake.reason} />
                <KV k="SSN on file" v={intake.ssn || "—"} mono />
              </>
            ) : (
              <>
                <KV k="Filing status" v={FILING_LABELS[intake.filing_status] || intake.filing_status || "—"} />
                <KV k="Dependents" v={(intake.dependents || []).length} />
                <KV k="SSN on file" v={intake.ssn || "—"} mono />
                <KV k="Bank account" v={intake.bank_account || "—"} mono />
              </>
            )}
            <KV k="Documents uploaded" v={`${totalDocs} across ${Object.keys(intake.uploads || {}).length} categor${Object.keys(intake.uploads || {}).length === 1 ? "y" : "ies"}`} />
          </div>
        </Card>

        {/* Uploaded documents */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="label-caps text-[10px]">Your documents</div>
            <span className="text-[10px] text-muted-foreground">{totalDocs} file{totalDocs === 1 ? "" : "s"}</span>
          </div>
          {totalDocs === 0 ? (
            <div className="text-sm text-muted-foreground">No documents on file.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="portal-docs">
              {Object.entries(intake.uploads || {}).map(([k, files]) => (
                files?.length ? (
                  <div key={k} className="border border-border rounded p-3">
                    <div className="text-xs font-medium mb-1">{uploadLabels[k] || k}</div>
                    <div className="space-y-1">
                      {files.map((p, i) => (
                        <a key={i} href={`${BACKEND_URL}/api/files/${p}`} target="_blank" rel="noreferrer" className="block text-xs underline text-primary truncate" data-testid={`portal-doc-${k}-${i}`}>
                          <FileText size={10} className="inline mr-1" /> Open file {i + 1}
                        </a>
                      ))}
                    </div>
                  </div>
                ) : null
              ))}
            </div>
          )}
        </Card>

        {intake.status === "completed" && (
          <div className="border border-primary/40 bg-primary/5 rounded-md p-5 flex items-start gap-3">
            <CheckCircle2 size={20} className="text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-serif text-xl mb-1">
                {isAmendment ? "Your amendment has been filed." : "Your return has been filed."}
              </div>
              <div className="text-sm text-muted-foreground">
                {isAmendment
                  ? "Amendment (1040-X) processing typically takes 8-12 weeks. We'll post an update here when the IRS confirms."
                  : "Your preparer will follow up with any final IRS confirmation."}
              </div>
            </div>
          </div>
        )}

        <PoweredBy className="text-center pt-4" />
      </section>
    </div>
  );
}

const FILING_LABELS = {
  single: "Single", mfj: "Married filing jointly", mfs: "Married filing separately",
  hoh: "Head of household", qw: "Qualifying surviving spouse",
};
const STAGE_LABELS = { received: "Received", in_progress: "In progress", filed: "Filed", refund_sent: "Refund sent", info: "Info" };
const REASON_LABELS = {
  missing_document: "Missing document",
  corrected_document: "Corrected document received",
  dependent_change: "Dependent change",
  filing_status_change: "Filing status change",
  income_change: "Income correction",
  credit_missed: "Missed credit or deduction",
  other: "Other",
};
const AMEND_UPLOAD_LABELS = {
  original_return: "Original return",
  corrected_document: "Corrected doc",
  supporting_docs: "Supporting docs",
};

function CashAdvanceCard({ intake }) {
  const decision = intake.cash_advance_decision || "pending";
  const meta = decision === "approved"
    ? { label: "Approved", tone: "border-primary/40 bg-primary/5", pill: "bg-primary/15 text-primary" }
    : decision === "denied"
      ? { label: "Not approved", tone: "border-destructive/40 bg-destructive/5", pill: "bg-destructive/15 text-destructive" }
      : { label: "Pending review", tone: "border-border bg-card", pill: "bg-secondary text-foreground" };
  return (
    <div className={`rounded-md border p-5 ${meta.tone}`} data-testid="portal-cash-advance">
      <div className="flex items-center gap-2 mb-3 text-primary">
        <DollarSign size={14} />
        <span className="label-caps text-[10px]">Cash advance · Refund Advantage / Pathway</span>
      </div>
      <div className="flex items-center flex-wrap gap-3 mb-2">
        <span className={`label-caps text-[10px] px-3 py-1 rounded ${meta.pill}`} data-testid="portal-cash-status">{meta.label}</span>
        {decision === "approved" && (
          <span className="font-serif text-2xl text-primary" data-testid="portal-cash-amount">
            ${Number(intake.cash_advance_amount_approved || 0).toLocaleString()}
          </span>
        )}
      </div>
      {decision === "pending" && (
        <p className="text-xs text-muted-foreground">
          You applied for a cash advance. Refund Advantage decides once your return is filed and accepted by the IRS — usually within a day. We'll update this card and post a message when they respond.
        </p>
      )}
      {decision === "approved" && (
        <p className="text-xs text-muted-foreground">
          Refund Advantage approved your advance. Funds are typically deposited within 24 hours of IRS acceptance. Check the updates above for details from your preparer.
        </p>
      )}
      {decision === "denied" && (
        <p className="text-xs text-muted-foreground">
          Refund Advantage did not approve the advance this time — no fault of {intake.first_name && "yours"}. Your full refund will still be direct-deposited on the normal IRS schedule (typically 21 days).
        </p>
      )}
      {intake.cash_advance_decision_notes && (
        <p className="text-xs italic mt-2">"{intake.cash_advance_decision_notes}"</p>
      )}
      <p className="text-[10px] text-muted-foreground mt-3">
        Cash advance decisions are made by Refund Advantage (Pathway). Approval, amount, and timing are set by them — not by us.
      </p>
    </div>
  );
}

const KV = ({ k, v, mono }) => (
  <div>
    <div className="label-caps text-[9px]">{k}</div>
    <div className={`text-sm ${mono ? "font-mono" : ""}`}>{v || "—"}</div>
  </div>
);

function ReferralCard({ intake }) {
  const code = intake._access_code;
  const link = code ? `${window.location.origin}/intake?ref=${code}` : "";
  const credit = Number(intake.credit_balance || 0);
  const events = intake.referral_credits_events || [];
  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Referral link copied");
    } catch { toast.error("Copy failed — long-press to copy"); }
  };
  return (
    <div className="rounded-md border border-primary/40 bg-primary/5 p-5" data-testid="portal-referral">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-primary">
          <Gift size={14} />
          <span className="label-caps text-[10px]">Refer a friend · $25 each</span>
        </div>
        <div className="text-right">
          <div className="label-caps text-[9px] text-muted-foreground">Credit on file</div>
          <div className="font-serif text-2xl text-primary" data-testid="portal-credit-balance">${credit.toLocaleString()}</div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Send a friend your link. Once their return is filed with us, you both get <strong>$25 off</strong> your next return. No cap on how many friends you can refer.
      </p>

      {link && (
        <div className="rounded-md bg-background border border-border p-3 mb-3 flex items-center gap-2 min-w-0" data-testid="portal-referral-link-row">
          <code className="text-xs truncate flex-1 min-w-0 text-foreground" data-testid="portal-referral-link">{link}</code>
          <Button size="sm" variant="outline" onClick={copyLink} data-testid="portal-referral-copy" className="shrink-0">
            <Copy size={12} className="mr-1" /> Copy
          </Button>
        </div>
      )}
      {events.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Credit history · {events.length} event{events.length === 1 ? "" : "s"}</summary>
          <ul className="mt-2 space-y-1 pl-3 border-l-2 border-primary/40">
            {events.slice().reverse().map((e) => (
              <li key={e.id} className="flex justify-between gap-2">
                <span>{e.kind === "earned_as_referrer" ? "Friend you referred filed" : e.kind === "earned_as_referred" ? "You filed with a friend's code" : e.kind}</span>
                <span className="text-primary font-medium">+${e.amount}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
